<template>
  <div class="c-grid">
    <div class="c-grid__items">
      <div class="c-grid__item c-grid__item__s__12 c-grid__item__m__7 c-grid__item__l__7">
        <BorderBox :title="$t('components.CompaniesSurveyTemplateForm.basic.templateName.title')" class="box">
          <Info>
            {{ $t('components.CompaniesSurveyTemplateForm.basic.templateName.info') }}
          </Info>
          <div>
            <ValidationProvider
              v-slot="{ errors }"
              v-model="templateName"
              :name="$t('components.CompaniesSurveyTemplateForm.basic.formLabels.templateName')"
              :rules="'required|max:' + maxLength['templateName']"
            >
              <label class="c-formLabel">
                {{ labelTextFormat({ label: 'templateName', max: maxLength['templateName'], required: true }) }}
              </label>
              <TextField
                :value="templateName"
                class="c-textField"
                :placeholder="$t('messages.noSettings')"
                @input="e => changeBasicAttribute(e.target.value, 'templateName')"
              />
              <ValidationError :error="errors[0]" />
              <p>{{ $t('components.CompaniesSurveyTemplateForm.basic.templateName.example') }}</p>
            </ValidationProvider>
          </div>
        </BorderBox>
        <BorderBox :title="$t('components.CompaniesSurveyTemplateForm.basic.cover.title')" class="box">
          <Info>
            <p>{{ $t('components.CompaniesSurveyTemplateForm.basic.cover.info1') }}</p>
            <p>{{ $t('components.CompaniesSurveyTemplateForm.basic.cover.info2') }}</p>
          </Info>
          <div>
            <input
              id="checkbox__showCoverForm"
              :checked="showCoverForm"
              class="c-checkbox__input"
              type="checkbox"
              @change="() => changeBasicAttribute(!showCoverForm, 'showCoverForm')"
            >
            <label class="c-checkbox__label" for="checkbox__showCoverForm">
              {{ $t('components.CompaniesSurveyTemplateForm.basic.cover.check') }}
            </label>
          </div>
          <template v-if="showCoverForm">
            <MediaImage
              class="cover__form"
              :max-files="1"
              :images="images"
              :loading="loading"
              @drop-images="onDropMediaImage"
              @remove-image="onRemoveMediaImage"
            />
            <KkTooltip :label="$t('labels.imageTooltipContent.title')"
                       top="25px"
                       width="400px"
                       :is-visible="isShowHoverImageTooltip"
                       @show-tooltip="showHoverImageTooltip"
                       @hide-tooltip="hideHoverImageTooltip"
            >
              <KkImageTooltipContent count="1" format="JPG,JPEG,PNG" width="400" height="300" size="5" :is-visible-resize="false" />
            </KkTooltip>
            <div class="cover__form">
              <ValidationProvider
                v-slot="{ errors }"
                v-model="coverTitle"
                :name="$t('components.CompaniesSurveyTemplateForm.basic.formLabels.coverTitle')"
                :rules="'required|max:' + maxLength['coverTitle']"
              >
                <label class="c-formLabel">
                  {{ labelTextFormat({ label: 'coverTitle', max: maxLength['coverTitle'], required: true }) }}
                </label>
                <TextField
                  :value="coverTitle"
                  class="c-textField"
                  :placeholder="$t('messages.noSettings')"
                  @input="e => changeBasicAttribute(e.target.value, 'coverTitle')"
                />
                <ValidationError :error="errors[0]" />
              </ValidationProvider>
            </div>
            <div class="cover__form">
              <ValidationProvider
                v-slot="{ errors }"
                v-model="coverText"
                :name="$t('components.CompaniesSurveyTemplateForm.basic.formLabels.coverText')"
                :rules="'required|max:' + maxLength['coverText']"
              >
                <label class="c-formLabel">
                  {{ labelTextFormat({ label: 'coverText', max: maxLength['coverText'], required: true }) }}
                </label>
                <textarea
                  :value="coverText"
                  class="c-textArea"
                  :placeholder="$t('messages.noSettings')"
                  rows="5"
                  @input="e => changeBasicAttribute(e.target.value, 'coverText')"
                />
                <ValidationError :error="errors[0]" />
              </ValidationProvider>
            </div>
          </template>
        </BorderBox>
        <BorderBox v-if="canUseEmail" :title="$t('components.CompaniesSurveyTemplateForm.basic.email.title')" class="box">
          <div>
            <input
              id="checkbox__showEmailForm"
              :checked="showEmailForm"
              class="c-checkbox__input"
              type="checkbox"
              @change="() => changeBasicAttribute(!showEmailForm, 'showEmailForm')"
            >
            <label class="c-checkbox__label" for="checkbox__showEmailForm">
              {{ $t('components.CompaniesSurveyTemplateForm.basic.email.check') }}
            </label>
          </div>
          <template v-if="showEmailForm">
            <div class="email__form">
              <ValidationProvider
                v-slot="{ errors }"
                v-model="supportEmail"
                :name="$t('components.CompaniesSurveyTemplateForm.basic.formLabels.supportEmail')"
                rules="required"
              >
                <label class="c-formLabel">
                  {{ labelTextFormat({ label: 'supportEmail', required: true }) }}
                </label>
                <TextField
                  :value="supportEmail"
                  class="c-textField"
                  :placeholder="$t('messages.noSettings')"
                  @input="e => changeBasicAttribute(e.target.value, 'supportEmail')"
                />
                <ValidationError :error="errors[0]" />
              </ValidationProvider>
              <p class="email__info">{{ $t('components.CompaniesSurveyTemplateForm.basic.email.info') }}</p>
              <div class="email__check">
                <input
                  id="checkbox__requiredEmail"
                  :checked="requiredEmail"
                  class="c-checkbox__input"
                  type="checkbox"
                  @change="() => changeBasicAttribute(!requiredEmail, 'requiredEmail')"
                >
                <label class="c-checkbox__label" for="checkbox__requiredEmail">
                  {{ $t('components.CompaniesSurveyTemplateForm.required') }}
                </label>
              </div>
              <div class="email__check">
                <input
                  id="checkbox__privacyPolicy"
                  :checked="showPrivacyPolicyForm"
                  class="c-checkbox__input"
                  type="checkbox"
                  @change="() => changeBasicAttribute(!showPrivacyPolicyForm, 'showPrivacyPolicyForm')"
                >
                <label class="c-checkbox__label" for="checkbox__privacyPolicy">
                  {{ $t('components.CompaniesSurveyTemplateForm.basic.privacyPolicy.check') }}
                </label>
              </div>
              <div v-if="showPrivacyPolicyForm" class="cover__form">
                <label class="c-formLabel">
                  {{ labelTextFormat({ label: 'privacyPolicyBody', required: false }) }}{{ $t('labels.optionalLabel') }}
                </label>
                <textarea
                  :value="privacyPolicyBody"
                  class="c-textArea"
                  :placeholder="$t('messages.noSettings')"
                  rows="5"
                  @input="e => changeBasicAttribute(e.target.value, 'privacyPolicyBody')"
                />
              </div>
            </div>
          </template>
        </BorderBox>
      </div>
      <div class="c-grid__item c-grid__item__s__12 c-grid__item__m__5 c-grid__item__l__5">
        <BorderBox>
          <p>
            {{ $t('components.CompaniesSurveyTemplateForm.basic.brand.selectedBrand') }}
            <span class="fontBold">
              {{ selectingBrandLength }}
            </span>
          </p>
          <div class="c-button c-button--icon">
            <button
              class="c-button__item c-button__item--primary"
              type="button"
              @click="openSelectBrandsModal"
            >
              <Icon name="iconPlus" class="imageAdd__icon" />
              {{ $t('components.CompaniesSurveyTemplateForm.basic.brand.select') }}
            </button>
          </div>
          <Info v-if="isPublished">
            <p>{{ $t('components.CompaniesSurveyTemplateForm.basic.private.info1') }}</p>
            <p>{{ $t('components.CompaniesSurveyTemplateForm.basic.private.info2') }}</p>
          </Info>
          <Info v-else>
            <p>{{ $t('components.CompaniesSurveyTemplateForm.basic.info1') }}</p>
            <p>{{ $t('components.CompaniesSurveyTemplateForm.basic.info2') }}</p>
            <p>{{ $t('components.CompaniesSurveyTemplateForm.basic.info3') }}</p>
          </Info>
          <BorderBox>
            <input
              id="checkbox__changePublishState"
              :checked="changePublishState"
              class="c-checkbox__input"
              type="checkbox"
              @change="() => changeBasicAttribute(!changePublishState, 'changePublishState')"
            >
            <label class="c-checkbox__label" for="checkbox__changePublishState">
              {{ $t('components.CompaniesSurveyTemplateForm.basic.formLabels.' + publishLabelName) }}
            </label>
          </BorderBox>
        </BorderBox>
      </div>
    </div>
    <KkSelectModal
      v-if="showBrandsModal"
      :is-visible="showBrandsModal"
      :header-label="$t('components.CompaniesSurveyTemplateForm.basic.brand.selectLabel')"
      :select-label="$t('components.CompaniesSurveyTemplateForm.basic.brand.targetBrand')"
      :list="companyBrandList"
      :selected-list="selectedBrandIds"
      @close="closeSelectBrandsModal"
      @confirm="confirmSelectBrandsModal"
    />
  </div>
</template>

<script>
import Icon from '../Icon'
import { mapState, mapActions } from 'vuex'
import BorderBox from './BorderBox'
import TextField from '../TextField'
import ValidationError from '../ValidationError'
import Info from '../Info'
import MediaImage from '../MediaImage'
import { basicDateFormat } from '../../modules/date'
import { KkSelectModal } from '../projects'
import { KkTooltip, KkImageTooltipContent } from '../base'

export default {
  name: 'CompaniesSurveyTemplateFormBasic',
  components: {
    Icon,
    BorderBox,
    Info,
    TextField,
    ValidationError,
    MediaImage,
    KkSelectModal,
    KkTooltip,
    KkImageTooltipContent,
  },
  data() {
    return {
      showBrandsModal: false,
      isShowHoverImageTooltip: false,
    }
  },
  computed: {
    ...mapState({
      meta: state => state.companiesCustomSurvey.meta,
      templateName: state => state.companiesCustomSurvey.data.basic.templateName,
      showCoverForm: state => state.companiesCustomSurvey.data.basic.showCoverForm,
      mediaImage: state => state.companiesCustomSurvey.data.basic.mediaImage,
      coverTitle: state => state.companiesCustomSurvey.data.basic.coverTitle,
      coverText: state => state.companiesCustomSurvey.data.basic.coverText,
      showEmailForm: state => state.companiesCustomSurvey.data.basic.showEmailForm,
      supportEmail: state => state.companiesCustomSurvey.data.basic.supportEmail,
      requiredEmail: state => state.companiesCustomSurvey.data.basic.requiredEmail,
      showPrivacyPolicyForm: state => state.companiesCustomSurvey.data.basic.showPrivacyPolicyForm,
      privacyPolicyBody: state => state.companiesCustomSurvey.data.basic.privacyPolicyBody,
      changePublishState: state => state.companiesCustomSurvey.data.basic.changePublishState,
      loading: state => state.companiesMediaImage.loading || state.companiesCustomSurvey.loading,
      tmpMediaImages: state => state.companiesMediaImage.images,
      company: state => state.companiesCompany.data,
      brands: state => state.companiesCustomSurvey.data.brand.brands,
      selectedBrandIds: state => state.companiesCustomSurvey.data.brand.brandIds,
      companyBrands: state => state.companiesBrands.data,
    }),
    companyUuid() {
      return this.$route.params.company_uuid
    },
    maxLength() {
      return {
        templateName: 40,
        coverTitle: 190,
        coverText: 400,
      }
    },
    isPublished() {
      return this.meta.status === 'published'
    },
    publishLabelName() {
      return this.isPublished ? 'close' : 'publish'
    },
    images() {
      return this.mediaImage ? [this.mediaImage] : []
    },
    companyBrandList() {
      return this.companyBrands ? this.companyBrands.map(b => b.attributes) : []
    },
    selectingBrandLength() {
      return this.selectedBrandIds.length == 0 ? this.$t('messages.notSelected') : this.$t('labels.itemCount', { count: this.selectedBrandIds.length })
    },
    selectBrandIds() {
      return this.brands ? this.brands.map(b => b.id) : []
    },
    canUseEmail() {
      return this.company.attributes.use_service !== 'localone'
    }
  },
  created() {
    this.readBrands(this.companyUuid)
    this.readPublishingCustomSurveyBrands()
    this.resetMediaImages()
    this.buildPrivacyPolicy()
    this.changeAttribute({ tab: 'brand', key: 'brandIds', value: this.selectBrandIds })
  },
  methods: {
    ...mapActions({
      changeAttribute: 'companiesCustomSurvey/changeAttribute',
      uploadMediaImages: 'companiesMediaImage/uploadMediaImages',
      deleteMediaImage: 'companiesMediaImage/deleteMediaImage',
      resetMediaImages: 'companiesMediaImage/resetMediaImages',
      readBrands: 'companiesBrands/readBrands',
      readPublishingCustomSurveyBrands: 'companiesPublishingCustomSurveyBrands/readPublishingCustomSurveyBrands',
    }),
    labelTextFormat({ label, max = 0, required = true }) {
      let text = this.$t('components.CompaniesSurveyTemplateForm.basic.formLabels.' + label)
      if (max !== 0) {
        text += this.$t('components.CompaniesSurveyTemplateForm.labelCount', { current: this[label].length , max: max })
      }
      return required ? text + this.$t('labels.requiredLabel') : text
    },
    async onDropMediaImage(images) {
      await this.uploadMediaImages(images)
      await this.changeBasicAttribute(this.tmpMediaImages[0], 'mediaImage')
      this.resetMediaImages()
    },
    onRemoveMediaImage(imageId) {
      this.deleteMediaImage(imageId)
      this.changeBasicAttribute(null, 'mediaImage')
    },
    changeBasicAttribute(value, key) {
      this.changeAttribute({ tab: 'basic', key: key, value: value })
    },
    buildPrivacyPolicy() {
      let value = this.privacyPolicyBody
      if (value === '') {
        const newDate = new Date()
        value = this.$t('components.CompaniesSurveyTemplateForm.basic.privacyPolicy.default', { companyName: this.company.attributes.name, createdAt: basicDateFormat(newDate)})
      } else {
        this.changeBasicAttribute(true, 'showPrivacyPolicyForm')
      }
      this.changeAttribute({ tab: 'basic', key: 'privacyPolicyBody', value: value })
    },
    openSelectBrandsModal() {
      this.showBrandsModal = true
    },
    closeSelectBrandsModal() {
      this.showBrandsModal = false
    },
    confirmSelectBrandsModal(selectingBrandIds) {
      this.changeAttribute({ tab: 'brand', key: 'brandIds', value: selectingBrandIds })
      this.showBrandsModal = false
    },
    showHoverImageTooltip() {
      this.isShowHoverImageTooltip = true
    },
    hideHoverImageTooltip() {
      this.isShowHoverImageTooltip = false
    }
  }
}
</script>

<style lang="scss" scoped>
.box:not(:first-child) {
  margin-top: 32px;
}
.cover__form {
  margin-top: 32px;
}
.email {
  &__form {
    margin-top: 32px;
  }
  &__check {
    margin-top: 24px;
  }
  &__info {
    margin-top: 24px;
  }
}
.c-button {
  &__image {
    display: block;
    width: 100%;
    height: 100%;
    margin-bottom: 8px;
    object-fit: cover;
    border: 1px solid #E3E3E3;
    border-radius: 4px;
  }
}
.c-button:not(:first-child) {
  margin-top: 0px;
}
.imageAdd {
  &__icon {
    width: 16px;
    margin-right: 8px;
  }
}
</style>
